import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { firestore } from '@/firebase'
import { useCustomerStore } from '@/stores/customer'
import createLogger from '@/utils/logger'
import { consultationDocHidesNpConsultBanner } from '@/stores/customerClinicalConsultationPresenceLogic'

const logger = createLogger('stores/customerClinicalConsultationPresence.ts')

export const useCustomerClinicalConsultationPresenceStore = defineStore(
  'customerClinicalConsultationPresence',
  () => {
    const customerStore = useCustomerStore()
    const hasLoaded = ref(false)
    const hasScheduledOrCompleted = ref(false)

    let unsubscribeQuery: (() => void) | null = null

    function clearState(): void {
      hasLoaded.value = false
      hasScheduledOrCompleted.value = false
    }

    function teardown(): void {
      unsubscribeQuery?.()
      unsubscribeQuery = null
      clearState()
    }

    function setupForCustomer(customerId: string): void {
      teardown()
      const consultationsQuery = query(
        collection(firestore, 'consultations'),
        where('customerId', '==', customerId),
        where('featureKey', '==', 'clinical_consultation'),
      )
      unsubscribeQuery = onSnapshot(
        consultationsQuery,
        (snap) => {
          hasLoaded.value = true
          hasScheduledOrCompleted.value = snap.docs.some((docSnap) =>
            consultationDocHidesNpConsultBanner(docSnap.data()),
          )
        },
        (err) => {
          logger.error('[clinical-consultation-presence] consultations query listener failed', err)
          hasLoaded.value = false
          hasScheduledOrCompleted.value = true
        },
      )
    }

    watch(
      () => customerStore.customer?.id ?? null,
      (customerId) => {
        if (!customerId) {
          teardown()
          return
        }
        setupForCustomer(customerId)
      },
      { immediate: true },
    )

    const canShowNpConsultBanner = computed(() => hasLoaded.value && !hasScheduledOrCompleted.value)

    function signOut(): void {
      teardown()
    }

    return {
      hasLoaded,
      hasScheduledOrCompleted,
      canShowNpConsultBanner,
      signOut,
    }
  },
)
